/**
 * ExitIntentOffer — a one-time prompt shown when a visitor is about to leave the
 * waitlist page, offering the joining incentive (free setup up to 4 weeks +
 * 14-day trial) to rescue the lead. Fires once per browser session.
 *
 * Exit detection:
 *  • Desktop — pointer leaves the top of the viewport (classic exit intent).
 *  • Mobile  — a back-navigation trap (pushState + popstate) plus a fast
 *    upward scroll toward the address bar; mobile has no mouseleave.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift, X, ArrowRight, Loader2, CheckCircle2, ShieldCheck } from 'lucide-react';
import { getAdAttribution } from '@/utils/gclid';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = 'corvana_exit_offer_shown';

export default function ExitIntentOffer({ seedEmail = '', seedCompany = '' }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(seedEmail);
  const [company, setCompany] = useState(seedCompany);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const firedRef = useRef(false);

  const trigger = () => {
    if (firedRef.current) return;
    try { if (sessionStorage.getItem(SESSION_KEY)) return; } catch { /* ignore */ }
    firedRef.current = true;
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch { /* ignore */ }
    setOpen(true);
  };

  useEffect(() => {
    // Don't arm until the visitor has been on the page a moment (avoids firing
    // on an accidental early flick).
    let armed = false;
    const armTimer = setTimeout(() => { armed = true; }, 4000);

    // Desktop: pointer exits through the top edge.
    const onMouseOut = (e) => {
      if (!armed) return;
      if (!e.relatedTarget && e.clientY <= 0) trigger();
    };

    // Mobile: back-button trap. Push a dummy state; intercept the first back.
    let lastY = window.scrollY;
    const onScroll = () => {
      if (!armed) return;
      const y = window.scrollY;
      // fast upward fling near the top → likely reaching for the back/close
      if (lastY - y > 60 && y < 120) trigger();
      lastY = y;
    };
    const onPopState = () => { if (armed && !firedRef.current) { trigger(); } };

    try { window.history.pushState({ corvanaExit: true }, ''); } catch { /* ignore */ }

    document.addEventListener('mouseout', onMouseOut);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('popstate', onPopState);
    return () => {
      clearTimeout(armTimer);
      document.removeEventListener('mouseout', onMouseOut);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('popstate', onPopState);
    };
  }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!/\S+@\S+\.\S+/.test(email) || !company.trim()) {
      setError('Add your work email and business name to lock in your spot.');
      return;
    }
    setError(''); setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/waitlist/submit`, {
        email, company_name: company,
        issues_comment: 'Saved via exit-intent offer on the waitlist page.',
        hp_url: '',
        ...getAdAttribution(),
      });
      setDone(true);
    } catch (err) {
      setError(err?.response?.status === 429
        ? 'You’re going a little fast — please try again in a minute.'
        : 'Something went wrong. Please try again.');
    }
    setSubmitting(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={() => setOpen(false)} data-testid="exit-intent-overlay"
        >
          <motion.div
            className="relative w-full max-w-sm rounded-3xl bg-white shadow-2xl overflow-hidden"
            initial={{ opacity: 0, scale: 0.94, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }} transition={{ type: 'spring', damping: 24, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()} data-testid="exit-intent-modal"
          >
            <button onClick={() => setOpen(false)} data-testid="exit-intent-close"
              className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/80 hover:bg-slate-100 flex items-center justify-center text-slate-500">
              <X className="w-4 h-4" />
            </button>

            {done ? (
              <div className="p-8 text-center" data-testid="exit-intent-success">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100 mb-4">
                  <CheckCircle2 className="w-7 h-7 text-emerald-600" />
                </div>
                <p className="text-xl font-semibold">Your spot is saved 🎉</p>
                <p className="text-slate-500 text-sm mt-2">
                  We’ll be in touch to hand-set-up your account and start your free trial.
                </p>
                <Button onClick={() => setOpen(false)} className="mt-6 w-full h-11 bg-slate-900 hover:bg-slate-800" data-testid="exit-intent-done">
                  Done
                </Button>
              </div>
            ) : (
              <>
                <div className="bg-gradient-to-br from-[#0066CC] to-[#007AFF] text-white p-6">
                  <div className="flex items-start gap-3">
                    <Gift className="w-7 h-7 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-lg font-bold leading-snug">Before you go — your free setup is reserved</p>
                      <p className="text-white/85 text-sm mt-1">
                        Lock in your <span className="font-semibold">free setup period (up to 4 weeks)</span> plus a
                        <span className="font-semibold"> 14-day free trial</span>. No credit card.
                      </p>
                    </div>
                  </div>
                </div>
                <form onSubmit={save} className="p-6 space-y-3" data-testid="exit-intent-form">
                  <Input data-testid="exit-intent-email" type="email" inputMode="email" value={email}
                    onChange={(e) => setEmail(e.target.value)} placeholder="Work email *" className="h-12 text-base" />
                  <Input data-testid="exit-intent-company" value={company}
                    onChange={(e) => setCompany(e.target.value)} placeholder="Business name *" className="h-12 text-base" />
                  {error && <p className="text-sm text-rose-600" data-testid="exit-intent-error">{error}</p>}
                  <Button type="submit" disabled={submitting} data-testid="exit-intent-submit"
                    className="w-full h-12 text-base font-semibold gap-2 bg-[#0066CC] hover:bg-[#0055aa] text-white">
                    {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Save my spot <ArrowRight className="w-4 h-4" /></>}
                  </Button>
                  <p className="flex items-center justify-center gap-1.5 text-xs text-slate-400">
                    <ShieldCheck className="w-3 h-3" /> Setup on your real data · cancel anytime
                  </p>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
